// Mahjong solitaire (owner 2026-10-24: the biggest solitaire search left in Poland, .ai/next-game-research.md). 144
// tiles face up in the Turtle, five layers. Two free tiles that match leave together for the discard pile (the one
// foundation): identical tiles, or any two flowers, or any two seasons. A tile is free when no tile lies on it (even
// partly) and its left or right side is open. Won when every tile is gone. Deals are always solvable; a player with no
// move left may shuffle the tiles still on the board (free). Owner's choices: .ai/game-rules.md "Mahjong".
//
// Each place of the layout is a tableau pile of 0 or 1 tile (as Pyramid's places), in the order of the layout's places.
// Six layouts: the Turtle, and the Tower for a board taller than wide (owner 2026-10-24: a phone or a tablet held
// upright, the Turtle's 15 tiles across made them tiny); the Fort, the Pyramid, the Cat and the Tree (owner 2026-10-27,
// picked from a prototype, branch prototype/mj-layouts). A new deal takes one of those that fit the board at random
// (board/geometry.ts mahjongLayoutsFor; GameState.layout) and the game keeps it to the end, also when the screen turns.
import type { CardKey, GameState, PileRef } from '../types';
import { card, pileCards, type VariantRules } from './base';

/** A place of the layout: its layer (0 = the table) and its top left corner in half tiles. */
export interface Place {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

/** Sorted into the drawing order (board.css z-index): layer by layer, each from left to right and top down, so a tile
 *  covers the right and lower edges of the tiles before it (the 3D sides show only on the outside). */
const drawingOrder = (places: Place[]): Place[] => places.sort((a, b) => a.z - b.z || a.x - b.x || a.y - b.y);

/** Tiles `from`..`to` (whole tiles) of a row at `y` (whole tiles, halves allowed) of layer `z`. */
function row(out: Place[], z: number, y: number, from: number, to: number) {
  for (let x = from; x <= to; x++) out.push({ z, x: 2 * x, y: 2 * y });
}

/**
 * The Turtle (the classic layout of Shanghai, 1986): layers of 87, 36, 16, 4 and 1 tiles on a table 15 tiles wide and
 * 8 high. Written from the shape itself (the GNOME map file is GPL and not copied). Rows of the table, in whole tiles:
 * 12, 8, 10, 12, 12, 10, 8, 12 tiles centred, plus one tile left and two right of the middle, half a row down.
 */
function turtle(): Place[] {
  const out: Place[] = [];
  [[1, 12], [3, 10], [2, 11], [1, 12], [1, 12], [2, 11], [3, 10], [1, 12]].forEach(([a, b], y) => row(out, 0, y, a!, b!));
  out.push({ z: 0, x: 0, y: 7 }, { z: 0, x: 26, y: 7 }, { z: 0, x: 28, y: 7 });
  for (let y = 1; y <= 6; y++) row(out, 1, y, 4, 9);
  for (let y = 2; y <= 5; y++) row(out, 2, y, 5, 8);
  for (let y = 3; y <= 4; y++) row(out, 3, y, 6, 7);
  out.push({ z: 4, x: 13, y: 7 });
  return drawingOrder(out);
}

/**
 * The Tower (ours, for an upright board): 8 tiles across and 11 down. Layers of 84 (the table without its four
 * corners: the deck before the deal and the discard pile stand there), 36, 20 and 4; the upper layers centred, half a
 * row down where their row count is even.
 */
function tower(): Place[] {
  const out: Place[] = [];
  for (let y = 0; y <= 10; y++) row(out, 0, y, y === 0 || y === 10 ? 1 : 0, y === 0 || y === 10 ? 6 : 7);
  for (let y = 2.5; y <= 7.5; y++) row(out, 1, y, 1, 6);
  for (let y = 3; y <= 7; y++) row(out, 2, y, 2, 5);
  for (let y = 4.5; y <= 5.5; y++) row(out, 3, y, 3, 4);
  return drawingOrder(out);
}

/** A layer drawn in whole tiles, '#' a tile, moved by `dx` / `dy` half tiles. */
function drawn(out: Place[], z: number, rows: string[], dx = 0, dy = 0) {
  rows.forEach((line, r) => [...line].forEach((ch, c) => ch === '#' && out.push({ z, x: 2 * c + dx, y: 2 * r + dy })));
}

/** The Fort (ours), 14 x 10: a wall two tiles thick with a gate in the top and one a side (half a row down), four
 *  corner towers three high, a keep four high. Layers of 88, 32, 20 and 4. */
function fort(): Place[] {
  const out: Place[] = [];
  drawn(out, 0, [
    '.#####..#####.',
    '.############.',
    '.##........##.',
    '.##..####..##.',
    '.##..####..##.',
    '.##..####..##.',
    '.##..####..##.',
    '.##........##.',
    '.############.',
    '.############.',
  ]);
  const towers = ['.##........##.', '.##........##.'];
  for (const z of [1, 2]) {
    drawn(out, z, towers);
    drawn(out, z, towers, 0, 16);
  }
  drawn(out, 1, ['####', '####', '####', '####'], 10, 6);
  drawn(out, 2, ['##', '##'], 12, 10);
  drawn(out, 3, ['##', '##'], 12, 10);
  out.push({ z: 0, x: 0, y: 9 }, { z: 0, x: 26, y: 9 });
  return drawingOrder(out);
}

/** The Pyramid (ours), 16 x 8: a stepped triangle seen from the front, rows of 2 to 16 tiles; each layer starts two
 *  rows lower, so its foot is five layers high. Layers of 72, 42, 20, 6 and 4. */
function pyramid(): Place[] {
  const out: Place[] = [];
  const triangle = (z: number, top: number) => {
    for (let y = top; y <= 7; y++) row(out, z, y, 7 - (y - top), 8 + (y - top));
  };
  [0, 2, 4, 6].forEach((top, z) => triangle(z, top));
  row(out, 4, 7, 6, 9);
  return drawingOrder(out);
}

/** The Cat (ours), 9 x 12, for an upright board: ears, a head, a body sitting. Layers of 82, 43, 18 and 1. */
function cat(): Place[] {
  const out: Place[] = [];
  drawn(out, 0, [
    '.#.....#.',
    '.##...##.',
    '.#######.',
    '.#######.',
    '.#######.',
    '..#####..',
    '.#######.',
    '#########',
    '#########',
    '#########',
    '#########',
    '.#######.',
  ]);
  for (let y = 2; y <= 4; y++) row(out, 1, y, 2, 6);
  for (let y = 7; y <= 10; y++) row(out, 1, y, 1, 7);
  for (let y = 8; y <= 10; y++) row(out, 2, y, 2, 6);
  row(out, 2, 3, 3, 5);
  row(out, 3, 9, 4, 4);
  return drawingOrder(out);
}

/** The Tree (ours), 11 x 13, for an upright board: three tiers and a trunk. Layers of 81, 56 (each row one tile
 *  narrower a side) and 7 (down the middle). */
function tree(): Place[] {
  const out: Place[] = [];
  const widths = [1, 3, 5, 7, 5, 7, 9, 11, 7, 9, 11, 3, 3];
  widths.forEach((w, y) => row(out, 0, y, 5 - (w - 1) / 2, 5 + (w - 1) / 2));
  widths.forEach((w, y) => w > 1 && row(out, 1, y, 6 - (w - 1) / 2, 4 + (w - 1) / 2));
  [3, 5, 6, 7, 8, 9, 10].forEach((y) => row(out, 2, y, 5, 5));
  return drawingOrder(out);
}

export interface Layout {
  /** Mahjong's shapes; `wide` and `tall` are Mahjong Connect's grids (mahjongConnect.ts). */
  readonly name: 'turtle' | 'tower' | 'fort' | 'pyramid' | 'cat' | 'tree' | 'wide' | 'tall';
  readonly places: readonly Place[];
  /** The table's size in half tiles. */
  readonly w: number;
  readonly h: number;
  /** How many layers lie on the table (the top layer's offset, geometry.ts). */
  readonly layers: number;
  /** Room kept free around the table, in tile faces (Connect: the joining line may run round the outside). */
  readonly margin?: number;
}

const sized = (name: Layout['name'], places: Place[]): Layout => ({
  name,
  places,
  w: Math.max(...places.map((p) => p.x + 2)),
  h: Math.max(...places.map((p) => p.y + 2)),
  layers: Math.max(...places.map((p) => p.z)),
});

/** GameState.layout indexes this list (a saved game keeps its number: add new layouts at the end). */
export const MAHJONG_LAYOUTS: readonly Layout[] = [
  sized('turtle', turtle()),
  sized('tower', tower()),
  sized('fort', fort()),
  sized('pyramid', pyramid()),
  sized('cat', cat()),
  sized('tree', tree()),
];
export const layoutOf = (s: { layout?: number }): Layout => MAHJONG_LAYOUTS[s.layout ?? 0] ?? MAHJONG_LAYOUTS[0]!;
/** The Turtle's places (the tests). */
export const MAHJONG_PLACES: readonly Place[] = MAHJONG_LAYOUTS[0]!.places;

const overlaps = (a: Place, b: Place) => Math.abs(a.x - b.x) < 2 && Math.abs(a.y - b.y) < 2;

/** Is the tile at place `i` of `places` free when `filled(j)` tells which places hold a tile? No tile lies on it, even
 *  partly, and its left or right side is open. */
export function isFreePlace(places: readonly Place[], i: number, filled: (j: number) => boolean): boolean {
  const p = places[i]!;
  let left = false;
  let right = false;
  for (let j = 0; j < places.length; j++) {
    if (j === i || !filled(j)) continue;
    const q = places[j]!;
    if (q.z === p.z + 1 && overlaps(p, q)) return false;
    if (q.z === p.z && Math.abs(q.y - p.y) < 2) {
      if (q.x === p.x - 2) left = true;
      else if (q.x === p.x + 2) right = true;
    }
  }
  return !left || !right;
}

export const isFree = (s: GameState, i: number): boolean => !!s.piles.tableau[i]?.length && isFreePlace(layoutOf(s).places, i, (j) => !!s.piles.tableau[j]?.length);

/** What a tile pairs with: the same id, any flower with any flower, any season with any season. */
export const matchClass = (id: string): string => (id[0] === 'f' || id[0] === 's' ? id[0] : id);
export const tilesMatch = (a: string, b: string): boolean => matchClass(a) === matchClass(b);

/** Every pair of free tiles that match (place indexes), for the hint and the "no moves" check. */
export function freePairs(s: GameState): Array<[number, number]> {
  const free = s.piles.tableau.map((_, i) => i).filter((i) => isFree(s, i));
  const out: Array<[number, number]> = [];
  for (let a = 0; a < free.length; a++)
    for (let b = a + 1; b < free.length; b++) {
      const ka = s.piles.tableau[free[a]!]![0]!;
      const kb = s.piles.tableau[free[b]!]![0]!;
      if (tilesMatch(card(s, ka).id, card(s, kb).id)) out.push([free[a]!, free[b]!]);
    }
  return out;
}

export const hasMove = (s: GameState): boolean => freePairs(s).length > 0;

/**
 * A solvable arrangement of `ids` over `places` (the place indexes to fill; ids.length === places.length): the game
 * played backwards. All the places count as filled; again and again two places that are free are given a matching
 * pair and emptied, so the pairs taken out in that order win the game. Picking two free places can end with one free
 * place left (a stack); then it starts over (`tries` times), and returns null if it never got through. `removals`: the
 * pairs of places in an order that wins (for the tests).
 */
export function solvableArrangement(ids: readonly string[], places: readonly number[], rng: () => number, layout: Layout = MAHJONG_LAYOUTS[0]!, tries = 50): { tiles: Map<number, string>; removals: Array<[number, number]> } | null {
  const pairs = tilePairs(ids, rng);
  if (!pairs) return null;
  for (let t = 0; t < tries; t++) {
    const order = shuffleList(pairs, rng);
    const left = new Set(places);
    const out = new Map<number, string>();
    const removals: Array<[number, number]> = [];
    let ok = true;
    for (const [a, b] of order) {
      const free = [...left].filter((i) => isFreePlace(layout.places, i, (j) => left.has(j)));
      if (free.length < 2) {
        ok = false;
        break;
      }
      const i = free[Math.floor(rng() * free.length)]!;
      const rest = free.filter((f) => f !== i);
      const j = rest[Math.floor(rng() * rest.length)]!;
      out.set(i, a);
      out.set(j, b);
      removals.push([i, j]);
      left.delete(i);
      left.delete(j);
    }
    if (ok) return { tiles: out, removals };
  }
  return null;
}

/** The tiles as pairs that match: identical tiles two by two, the flowers two by two, the seasons two by two (each
 *  group shuffled first, so the flowers pair at random); null when a group is odd. */
export function tilePairs(ids: readonly string[], rng: () => number): Array<[string, string]> | null {
  const groups = new Map<string, string[]>();
  for (const id of ids) groups.set(matchClass(id), [...(groups.get(matchClass(id)) ?? []), id]);
  const pairs: Array<[string, string]> = [];
  for (const g of groups.values()) {
    if (g.length % 2) return null;
    const shuffled = shuffleList(g, rng);
    for (let i = 0; i < shuffled.length; i += 2) pairs.push([shuffled[i]!, shuffled[i + 1]!]);
  }
  return pairs;
}

export function shuffleList<T>(list: readonly T[], rng: () => number): T[] {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** A new deal's tile ids, place by place (the deal turns them into a shuffle pattern, so Retry and the stored wins
 *  work as in the other games). A plain shuffle if the search never got through (it does in practice). */
function dealIds(base: readonly string[], rng: () => number, layout = 0): string[] {
  const l = MAHJONG_LAYOUTS[layout] ?? MAHJONG_LAYOUTS[0]!;
  const places = l.places.map((_, i) => i);
  const arranged = solvableArrangement(base, places, rng, l);
  return arranged ? places.map((i) => arranged.tiles.get(i)!) : shuffleList(base, rng);
}

/** The tiles still on the board, shuffled over the same places, solvable when it can be (a stack of two cannot):
 *  the free shuffle when no move is left (owner 2026-10-24). */
export function shuffledTableau(s: GameState, rng: () => number): Array<readonly CardKey[]> {
  const places = s.piles.tableau.map((_, i) => i).filter((i) => s.piles.tableau[i]!.length);
  const keys = places.map((i) => s.piles.tableau[i]![0]!);
  const ids = keys.map((k) => card(s, k).id);
  const arranged = solvableArrangement(ids, places, rng, layoutOf(s));
  const pool = keys.slice();
  const order = arranged ? places.map((i) => arranged.tiles.get(i)!) : shuffleList(ids, rng);
  const tableau: Array<readonly CardKey[]> = s.piles.tableau.map(() => []);
  places.forEach((place, n) => {
    const k = pool.findIndex((key) => card(s, key).id === order[n]);
    tableau[place] = [pool[k]!];
    pool.splice(k, 1);
  });
  return tableau;
}

/** The free tile at a place (a tableau pile), or undefined. */
function freeTile(s: GameState, pile: PileRef): CardKey | undefined {
  return pile.kind === 'tableau' && isFree(s, pile.index) ? s.piles.tableau[pile.index]![0] : undefined;
}

function canLift(s: GameState, pile: PileRef, index: number): boolean {
  return index === 0 && pileCards(s, pile).length === 1 && freeTile(s, pile) !== undefined;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const key = run[0];
  if (key === undefined || run.length !== 1) return false;
  const other = freeTile(s, target);
  return other !== undefined && other !== key && tilesMatch(card(s, key).id, card(s, other).id);
}

export const mahjong: VariantRules = {
  id: 'mahjong',
  columns: MAHJONG_PLACES.map(() => 1), // 144 places in every layout
  foundations: 1,
  cells: 0,
  decks: 1,
  suits: [],
  variableSuits: 0,
  turn: 1,
  hasWaste: false,
  recycle: false,
  pairs: true,
  tiles: true,
  allFaceUp: true,
  autoMove: false,
  foundationToTableau: false,
  canLift,
  canDrop,
  foundationFor: () => null,
  buildsOn: () => false,
  completedRun: () => false,
  isWon: (s) => s.piles.tableau.every((p) => p.length === 0),
  dealIds,
  layouts: MAHJONG_LAYOUTS,
  pairsLeft: freePairs,
  reshuffle: shuffledTableau,
};
